import { Button, Divider, Empty, message, Popover, Spin, Steps, Checkbox } from 'antd'
import styles from './index.module.scss'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import BaseInfoForm from './BaseInfoForm'
import SkuDetailForm from './SkuDetailForm'
import { CloseOutlined } from '@ant-design/icons'
import API from '@api/api'
import dayjs from 'dayjs'
import PSAIModal from "@PSAIComponents/PSAIModal";
import eventBus from '@utils/eventBus';
import Iconfont from '@utils/Iconfont';

function RequirementForm ({recordId, readOnly, draftDisabled, onSubmitOk}) {
    const baseInfoRef = useRef(null)
    const skuDetailRef = useRef(null)
    const [step, setStep] = useState(0)
    const [draftLoading, setDraftLoading] = useState(false)
    const [loading, setLoading] = useState(false)
    const [id, setId] = useState(null)
    const [status, setStatus] = useState(null)
    const [draftOpen, setDraftOpen] = useState(false)
    // 最新修改过任何字段，未保存为草稿
    const editedRef = useRef(false)
    // 获取需求详情
    const fetchDataById = useCallback(async id => {
        const res = await API.getRequirementById(id)
        if (res.code != 0) throw new Error(res.message)
        const data = res.data            
        baseInfoRef.current.setFieldsValue({
            id: data.id,
            deliveryVersion: data.deliveryVersion,
            description: data.description,
            refFiles: data.refFiles
        })
        skuDetailRef.current.setValues(data.skuItems)
        setBaseInfo({
            id: data.id,
            deliveryVersion: data.deliveryVersion,
            description: data.description,
            refFiles: data.refFiles
        })
        setSkuDetail(data.skuItems)
        return data
    }, [])

    // 加急提示
    const submitConfirm = useCallback((useUrgent) => {
        return new Promise((resolve, reject) => {
            const notPromptKey = 'urgent_not_prompt'
            let urgentNotice = false
            if (useUrgent) {
                const notPromptExpired = localStorage.getItem(notPromptKey)
                if (notPromptExpired && dayjs().isBefore(dayjs(notPromptExpired))) {
                    urgentNotice = false
                } else {
                    urgentNotice = true
                }
            } else {
                urgentNotice = false
            }
            
            // 勾选不再提示后当前自然日不再弹出
            let notPrompt = false
            PSAIModal.confirm({
                content: urgentNotice ? (
                    <div style={{display:"flex",flexDirection:"column",justifyContent:"flex-start", alignItems:"flex-start", gap: 8}}>
                        <span style={{fontSize: 16}}>确认提交需求？</span>
                        <span style={{fontSize: 14}}>加急的商品，将收取额外费用</span>
                        <Checkbox onChange={e => notPrompt = e.target.checked}>不再提示加急信息</Checkbox>
                    </div>
                ) : (
                    <div style={{display:"flex",flexDirection:"column",justifyContent:"flex-start", alignItems:"flex-start", gap: 8}}>
                        <span style={{fontSize: 16}}>确认提交需求？</span>
                        <span style={{fontSize: 14}}>提交后，我们将尽快为您处理</span>
                    </div>
                ),
                okText: "提交",
                onOk: () => {
                    if (notPrompt) {
                        localStorage.setItem(notPromptKey, dayjs().endOf('day').toString())
                    }
                    resolve(true)
                },
                onCancel: () => {
                    resolve(false)
                }
            })
        })
    }, [])

    // 存为草稿/提交需求
    const doSubmit = useCallback(async (newStatus) => {
        try {
            if (newStatus == -3) setDraftLoading(true)
            else setLoading(true)
            const [res1, res2] = await Promise.allSettled([
                baseInfoRef.current.validate(),
                newStatus == -3 ? skuDetailRef.current.getValues() : skuDetailRef.current.validate(),
            ])
            if (res1.status == "rejected") {
                setStep(0)
                return false
            }
            if (res2.status == "rejected") {
                setStep(1)
                return false
            }
            const baseInfo = res1.status == "fulfilled" ? res1.value : null
            const skuDetail = res2.status == "fulfilled" ? res2.value : null

            if (newStatus == -2) {
                const useUrgent = skuDetail.some(item => item.urgent)
                const res = await submitConfirm(useUrgent)
                if (!res) return false
            }

            // newStatus=-3存为草稿，-2提交需求
            if (id) {
                // 更新草稿：调用更新接口，status=-3；
                // 更新需求：调用更新接口，status=-2；
                // 提交草稿：先调用更新接口，status=-3，然后再调用提交需求；
                const res = await API.updateRequirement({
                    ...baseInfo,
                    id,
                    status: (status == -3 && newStatus == -2) ? -3 : newStatus, // -3草稿，-2待接单
                    skuItems: skuDetail,
                })
                if (res.code != 0) throw new Error(res.message)
                await fetchDataById(id)
                if (status == -3 && newStatus == -2) {
                    const res = await API.submitRequirement(id)
                    if (res.code != 0) throw new Error(res.message)
                    setStatus(newStatus)
                }
            } else {
                // 提交需求：先调用新建接口，status=-3，然后再调用提交需求；
                // 保存草稿：调用新建接口，status=-3；
                const res = await API.createRequirement({
                    ...baseInfo,
                    status: -3, // -3草稿，-2待接单
                    skuItems: skuDetail,
                })
                if (res.code != 0) throw new Error(res.message)
                const newId = res.data
                await fetchDataById(newId)
                if (newStatus == -2) {
                    const res = await API.submitRequirement(newId)
                    if (res.code != 0) throw new Error(res.message)
                    setId(newId)
                    setStatus(-2)
                } else {
                    setId(newId)
                    setStatus(-3)
                }
            }
            if (newStatus == -3) { 
                message.success('已存为草稿')
                editedRef.current = false
            } else {
                message.success('已收到您的需求，我们会尽快处理')
                onSubmitOk && onSubmitOk()
            }
            return true
        } catch (err) {
            console.log(err)
            message.warning(err.message)
            return false
        } finally {
            if (newStatus == -3) setDraftLoading(false)
            else setLoading(false)
        }
    }, [id, status, fetchDataById, onSubmitOk, submitConfirm])
    // 存为草稿
    const handleSave = useCallback(() => {
        doSubmit(-3)
    }, [doSubmit])
    // 提交需求
    const handleSubmit = useCallback(() => {
        doSubmit(-2)
    }, [doSubmit])

    // 编辑草稿
    const handleInit = useCallback(async id => {
        try {
            const data = await fetchDataById(id)
            setDraftOpen(false)
            setStep(0)
            setId(data.id)
            setStatus(data.status)
            editedRef.current = false
        } catch (err) {
            console.log(err)
            message.warning(err.message)
        }
    }, [fetchDataById])
    const handleEdit = useCallback(id => {
        eventBus.emit('requirementForm:edited', async () => {
            try {
                const data = await fetchDataById(id)
                setDraftOpen(false)
                setStep(0)
                setId(data.id)
                setStatus(data.status)
                editedRef.current = false
            } catch (err) {
                console.log(err)
                message.warning(err.message)
            }
        })
    }, [fetchDataById])
    const handleNew = useCallback(() => {
        eventBus.emit('requirementForm:edited', () => {
            baseInfoRef.current.reset()
            skuDetailRef.current.setValues([])
            setBaseInfo()
            setSkuDetail([])
            setId()
            setStatus()
            setStep(0)
        })
    }, [])
    // 删除草稿
    const handleDelete = useCallback(deleteId => {
        if (deleteId != id) return
        baseInfoRef.current.reset()
        skuDetailRef.current.setValues([])
        setBaseInfo()
        setSkuDetail([])
        setId()
        setStatus()
        setStep(0)
    }, [id])
    // 初始化需求详情
    useEffect(() => {
        if (!recordId) return
        handleInit(recordId)
    }, [handleInit])

    const [baseInfo, setBaseInfo] = useState()
    const [skuDetail, setSkuDetail] = useState([])
    const baseInfoChange = useCallback((changedValues, allValues) => {
        setBaseInfo(allValues)
        editedRef.current = true
    }, [])
    const skuDetailChange = useCallback((values) => {
        setSkuDetail(values)
        editedRef.current = true
    }, [])
    const nextStepDisabled = useMemo(() => {
        const deliveryVersion = (baseInfo?.deliveryVersion || "").trim()
        return !deliveryVersion
    }, [baseInfo])
    const submitDisabled = useMemo(() => {
        if (skuDetail.some(item => item.skuCode && item.skuImages?.length > 0 && item.imageCount > 0)) return false
        return true
    }, [skuDetail])

    useEffect(() => {
        const validateForm = (callback) => {
            if (!callback) return
            if (editedRef.current) {
                PSAIModal.confirm({
                    title: (
                        draftDisabled ?
                        <div>
                            <span style={{fontSize: 16}}>离开页面后，当前未提交的需求将不会保存</span><br />
                            <span style={{fontSize: 14}}>是否提交修改内容？</span>
                        </div> :
                        <div>
                            <span style={{fontSize: 16}}>离开页面后，当前未提交的需求将不会保存</span><br />
                            <span style={{fontSize: 14}}>是否将已填写的内容存为草稿？</span>
                        </div>
                    ),
                    okText: draftDisabled ? '提交' : '存为草稿',
                    cancelText: draftDisabled ? '直接离开' : '离开',
                    onOk: () => {
                        if (draftDisabled) {
                            return doSubmit(-2).then(res => {
                                if (res) callback && callback()
                            })
                        } else {
                            return doSubmit(-3).then(res => {
                                if (res) callback && callback()
                            })
                        }
                    },
                    onCancel: () => {
                        callback()
                    },
                    onClose: () => {}
                })
            } else {
                callback()
            }
        }
        eventBus.on('requirementForm:edited', validateForm)
        return () => {
            eventBus.off('requirementForm:edited', validateForm)
        }
    }, [doSubmit, draftDisabled])

    return (
        <div className={styles.RequirementForm}>
            <div className={styles.RequirementForm_title}>
                <span>提需求，</span>
                <span>收成片</span>
            </div>
            <div className={styles.RequirementForm_steps}>
                <Steps
                    style={{width: 400}}
                    size="small"
                    current={step}
                    onChange={nextStepDisabled ? null : setStep}
                    items={[{
                        title: '批次基本信息',
                        // status: step == 0 ? 'process' : 'wait',
                    }, {
                        title: 'SKU详情',
                        // status: step == 1 ? 'process' : 'wait',
                    }]}
                />
            </div>
            <div className={styles.RequirementForm_content}>
                <div className={styles.ContentWrap} style={{display: step != 0 ? 'none' : 'block'}}>
                    <BaseInfoForm ref={baseInfoRef} readOnly={readOnly} onChange={baseInfoChange} />
                </div>
                <div className={styles.ContentWrap} style={{display: step != 1 ? 'none' : 'block', maxWidth: 1440}}>
                    <SkuDetailForm ref={skuDetailRef} readOnly={readOnly} onChange={skuDetailChange} />
                </div>
            </div>
            <div className={styles.RequirementForm_footer}>
                <div className={styles.DraftButtonGroup}>
                {
                    (draftDisabled || readOnly) ? null : 
                    <>
                    <Popover
                        open={draftOpen}
                        onOpenChange={setDraftOpen}
                        placement="topLeft"
                        content={<DraftList onEdit={handleEdit} onClose={setDraftOpen} onDelete={handleDelete} />}
                        trigger="click"
                        arrow={false}
                        destroyOnHidden={true}
                        styles={{
                            container: {padding: 0}
                        }}
                    >
                        <Button style={{width: 148, height: 40}} icon={<Iconfont type="icon-draft" />}>草稿箱</Button>
                    </Popover>
                    <Button disabled={nextStepDisabled} style={{height: 40}} type="text" onClick={handleSave} loading={draftLoading}>存为草稿</Button>
                    <Button style={{height: 40}} type="text" onClick={handleNew} >新建表单</Button>
                    </>
                }
                </div>
                <div className={styles.ButtonGroup}>
                {
                    step == 0 ?
                    <Button disabled={nextStepDisabled} style={{width: 216, height: 40}} type="primary" onClick={() => setStep(step + 1)}>下一步</Button> :
                    <>
                    <Button style={{width: 216, height: 40}} onClick={() => setStep(step - 1)}>上一步</Button>
                    {readOnly ? null :<Button disabled={submitDisabled} style={{width: 216, height: 40}} type="primary" onClick={handleSubmit} loading={loading}>提交需求</Button>}
                    </>
                }
                </div>
            </div>
        </div>
    )
}

export default RequirementForm

function DraftList (props) {
    const [force, updateForce] = useState()
    const [loading, setLoading] = useState(false)
    const [draftList, setDraftList] = useState([])
    useEffect(() => {
        setLoading(true)
        API.getRequirementDraft().then(res => {
            console.log('getRequirementDraft', res)
            if (res.code !== 0) throw new Error(res.message)
            setDraftList(res.data)
        }).catch((err) => {
            console.error(err)
            message.warning(err.message)
        }).finally(() => {
            setLoading(false)
        })
    }, [force])

    const currentRef = useRef()
    const handleDelete = useCallback(id => {
        PSAIModal.confirm({
            title: `确定删除这个草稿吗？`,
            okText: '确定',
            cancelText: '取消',
            getContainer: currentRef.current,
            onOk () {
                return API.deleteRequirement(id).then(res => {
                    if (res.code === 0) {
                        props.onDelete(id)
                        message.success("删除成功")
                        updateForce({})
                    } else {
                        message.warning(res.message)
                    }                                
                })
            }
        })
    }, [])

    return (
        <div className={styles.DraftListWrap} ref={currentRef}>
            <div className={styles.DraftListTitle}>
                <div className={styles.DraftListTitleInner}>
                    <span style={{color: "#1D1B1B"}}>草稿箱</span>
                    <span style={{color: "#6A7282"}}>共{draftList.length}个草稿</span>
                </div>
                <CloseOutlined onClick={() => props.onClose(false)} />
            </div>
            <Divider style={{margin: 0}}/>
            <Spin spinning={loading}>
            <div className={styles.DraftListContent}>
                <div className={styles.DraftListInner}>
                {
                    loading ? null :
                    draftList.length == 0 ? (
                        <Empty
                            styles={{
                                root: {margin: 20},
                                image: {height: 64}
                            }}
                            description="暂无草稿"
                            image={<Iconfont type="icon-no-draft" style={{ fontSize: 64, color: "#D1D5DB" }} />}
                        />
                    ) :
                    draftList.map(item => (
                        <div key={item.id} className={styles.DraftItem}>
                            <div className={styles.DraftItemInfo}>
                                <div>{item.deliveryVersion}</div>
                                <div>
                                    <div>
                                        <span>SKU数:</span>
                                        <span style={{color: "#364153"}}>{item.skuItems.length}</span>
                                    </div>
                                    <span>{dayjs(item.updatedTime).format('YYYY-MM-DD HH:mm')}</span>
                                </div>
                            </div>
                            <div className={styles.DraftItemButtonGroup}>
                                <Iconfont type="icon-rewrite" style={{ fontSize: 20 }} onClick={() => props.onEdit(item.id)} />
                                <Iconfont type="icon-delete" style={{ fontSize: 20 }} onClick={() => handleDelete(item.id)} />
                            </div>
                        </div>
                    ))
                } 
                </div>
            </div>
            </Spin>
        </div>
    )

}